import * as React from 'react';
import * as ReactDOM from 'react-dom';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import getTodoItemViewState from '../selectors/getTodoItemViewState';
import isBeingEdited from '../selectors/isBeingEdited';
import TodoListItem, { TodoListItemProps } from './TodoListItem';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { reorderTodos } from '../actions/dropActions';
import { observer } from 'mobx-react';
import { TodoItemDragData } from 'owa-todo-types/lib/types/TodoItemDragData';
import { isBrowserFirefox } from 'owa-user-agent';

const styles = require('./TodoListItem.scss');
const classNames = require('classnames/bind').bind(styles);

export interface TodoListItemDraggableProps extends TodoListItemProps {
    forwardedRef?: React.Ref<TodoListItem>;
}

const DRAG_X_OFFSET = -10;
const DRAG_Y_OFFSET = 20;

@observer
export default class TodoListItemDraggable extends React.Component<TodoListItemDraggableProps, {}> {
    static defaultProps = {
        canDropOver: true,
    };

    render() {
        const { forwardedRef, todo, ...passThroughProps } = this.props;
        const todoViewState = getTodoItemViewState(todo.Id);
        const isEditing = isBeingEdited(todo.Id);

        return (
            <DragAndDroppable
                isDraggable={this.isDraggable}
                getDragData={this.getDragData}
                getDragPreview={this.getDragPreview}
                showDefaultNoDragImage={false}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                dragViewState={todoViewState.drag}
                dropViewState={todoViewState.drop}
                canDrop={this.canDrop.bind(this)}
                onDrop={this.onDrop.bind(this)}
                canDrag={() => !isEditing}
                classNames={classNames({
                    isBeingDragged: todoViewState.drag.isBeingDragged,
                })}>
                <TodoListItem ref={forwardedRef} todo={todo} {...passThroughProps} />
            </DragAndDroppable>
        );
    }

    public isDraggable = () => {
        /**
         * In Firefox, when a textarea is draggable, you are no longer able to click
         * to move the cursor within a textarea. This bug is tracked here: https://bugzilla.mozilla.org/show_bug.cgi?id=943944
         * A textarea is rendered by the EditableTitle component when isEditing is false,
         * so when isEditing, we want to disable dragging for the todo.
         */
        const todo = this.props.todo;
        const isEditing = isBeingEdited(todo.Id);
        return !(isBrowserFirefox() && isEditing);
    };

    private getDragData = () => {
        let { todo } = this.props;
        let dragData: TodoItemDragData = {
            itemType: DraggableItemTypes.Todo,
            ewsItemIds: [todo.Id],
            subjects: [todo.Subject],
        };

        return dragData;
    };

    private onDrop = (dragData: DragData, pageX: number, pageY: number, target?: HTMLElement) => {
        switch (dragData.itemType) {
            case DraggableItemTypes.Todo:
                const taskDragData = dragData as TodoItemDragData;
                if (taskDragData.ewsItemIds && taskDragData.ewsItemIds.length === 1) {
                    reorderTodos(taskDragData.ewsItemIds[0], this.props.todo.Id);
                }
                break;
        }
    };

    private canDrop = (dragInfo: DragData): boolean => {
        return this.props.canDropOver && dragInfo.itemType === DraggableItemTypes.Todo;
    };

    private getDragPreview = (todoDragData: TodoItemDragData) => {
        var elem = document.createElement('div');
        ReactDOM.render(
            <div className={styles.dragHelperStyle}>
                <TodoListItem
                    todo={this.props.todo}
                    canDropOver={false}
                    titleClassName={styles.dragTextWrapper}
                    checkboxClassName={styles.dragCheckboxClassName}
                />
            </div>,
            elem
        );

        return elem;
    };
}
